import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { HeaderBar } from "@/components/grc/HeaderBar";
import { WorkspaceNav } from "@/components/ropa/shared";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDateTime } from "@/components/grc/shared";
import { Sparkles, RefreshCw, Download, AlertTriangle, FileText } from "lucide-react";

const SEVERITY_BADGE = {
  CRITICAL: "bg-red-600 text-white",
  MAJOR: "bg-amber-500 text-white",
  MINOR: "bg-yellow-400 text-slate-900",
};

const Card = ({ title, testId, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
    <h3 className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">{title}</h3>
    {children}
  </div>
);

const Metric = ({ label, value, testId }) => (
  <div className="border border-slate-200 rounded-lg p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
    <div className="text-base font-bold text-[#003366] mt-0.5">{value}</div>
  </div>
);

export default function BoardMemo() {
  const { id } = useParams();
  const [memo, setMemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/grc/${id}/board-memo`);
      setMemo(data);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error("Could not load the board memo");
      setMemo(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/grc/${id}/board-memo/generate`);
      setMemo(data);
      toast.success("Board memo generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the board memo");
    } finally {
      setGenerating(false);
    }
  };

  const downloadDocx = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("grc_token");
      const res = await fetch(`${API_BASE}/grc/${id}/board-memo/export/docx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const disposition = res.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "CX0-BoardMemo.docx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("DOCX downloaded");
    } catch (e) {
      toast.error("Could not export the DOCX");
    } finally {
      setDownloading(false);
    }
  };

  const stats = memo?.stats || {};
  const narrative = memo?.narrative || {};
  const gs = stats.gap_summary || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="board-memo-page">
      <HeaderBar assessmentId={id} />
      <main className="max-w-[1000px] mx-auto px-4 md:px-8 py-6 space-y-6">
        <WorkspaceNav assessmentId={id} />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#003366]">Board Memo</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              One-page executive summary for leadership, generated from live compliance data.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {memo && (
              <Button
                variant="outline"
                size="sm"
                onClick={downloadDocx}
                disabled={downloading}
                data-testid="board-memo-download-docx"
              >
                <Download size={14} className="mr-1.5" /> {downloading ? "Preparing…" : "Download DOCX"}
              </Button>
            )}
            <Button
              size="sm"
              onClick={generate}
              disabled={generating}
              data-testid="board-memo-generate-button"
              className="bg-[#008080] hover:bg-[#1a51db]"
            >
              {memo ? <RefreshCw size={14} className="mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
              {generating ? "Generating…" : memo ? "Regenerate" : "Generate Board Memo"}
            </Button>
          </div>
        </div>

        {loading && (
          <div className="space-y-4" data-testid="board-memo-loading">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        )}

        {!loading && !memo && (
          <div
            className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center"
            data-testid="board-memo-empty-state"
          >
            <FileText className="mx-auto text-slate-300" size={32} />
            <div className="text-sm font-medium text-slate-500 mt-3">No board memo generated yet</div>
            <div className="text-xs text-slate-400 mt-1">
              Click "Generate Board Memo" to draft a one-page summary from this assessment's live data.
            </div>
          </div>
        )}

        {!loading && memo && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400" data-testid="board-memo-meta">
              <span>Generated {fmtDateTime(memo.generated_at)} by {memo.generated_by}</span>
              {memo.model_used === "template_fallback" && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800"
                  data-testid="board-memo-fallback-badge"
                >
                  <AlertTriangle size={11} /> AI unavailable — deterministic summary used
                </span>
              )}
            </div>

            <Card title="Executive summary" testId="board-memo-executive-summary">
              <p className="text-sm text-slate-700 leading-relaxed">{narrative.executive_summary}</p>
            </Card>

            <Card title="Overall posture" testId="board-memo-overall-posture">
              <p className="text-sm text-slate-700 leading-relaxed mb-4">{narrative.overall_posture}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Metric testId="board-memo-metric-score" label="Overall score" value={`${stats.overall_score ?? "—"}%`} />
                <Metric testId="board-memo-metric-maturity" label="Maturity" value={`L${stats.maturity_level ?? "—"}`} />
                <Metric testId="board-memo-metric-gaps" label="Critical / Major" value={`${gs.critical ?? 0} / ${gs.major ?? 0}`} />
                <Metric testId="board-memo-metric-deadline" label="Days to deadline" value={stats.days_until_deadline ?? "—"} />
                <Metric testId="board-memo-metric-ropa" label="RoPA coverage" value={`${stats.ropa_coverage_pct ?? 0}%`} />
                <Metric testId="board-memo-metric-conflicts" label="Open RoPA conflicts" value={stats.ropa_open_conflicts ?? 0} />
                <Metric testId="board-memo-metric-trend" label="Trend" value={`${stats.trend_direction ?? "flat"} (${stats.trend_delta_pct ?? 0}%)`} />
                <Metric testId="board-memo-metric-band" label="Readiness band" value={stats.readiness_band ?? "—"} />
              </div>
            </Card>

            <Card title="Key risks" testId="board-memo-key-risks">
              {(stats.top_findings || []).length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="board-memo-no-risks">
                  No open critical or major findings.
                </p>
              ) : (
                <ul className="space-y-3">
                  {stats.top_findings.map((f, i) => (
                    <li key={f.finding_id} className="flex items-start gap-3" data-testid={`board-memo-risk-${i}`}>
                      <span className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-semibold ${SEVERITY_BADGE[f.severity] || "bg-slate-200 text-slate-700"}`}>
                        {f.severity}
                      </span>
                      <div className="text-sm text-slate-700">
                        <span className="font-medium text-[#003366]">{f.title}</span>
                        <span className="text-slate-400"> ({f.pillar})</span>
                        <p className="text-xs text-slate-500 mt-0.5">{(narrative.key_risk_narratives || [])[i]}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Progress highlights" testId="board-memo-progress-highlights">
              <ul className="space-y-1.5 text-sm text-slate-700">
                {(narrative.progress_highlights || []).map((h, i) => (
                  <li key={i} data-testid={`board-memo-highlight-${i}`}>• {h}</li>
                ))}
              </ul>
            </Card>

            <Card title="Recommended actions" testId="board-memo-recommended-actions">
              <ul className="space-y-2 text-sm text-slate-700">
                {(narrative.recommended_actions || []).map((a, i) => (
                  <li key={i} data-testid={`board-memo-action-${i}`}>
                    <span className="font-medium text-[#003366]">{a.action}</span>
                    {a.rationale && <span className="text-slate-500"> — {a.rationale}</span>}
                  </li>
                ))}
              </ul>
            </Card>

            <p className="text-[11px] text-slate-400 italic" data-testid="board-memo-disclaimer">
              AI-assisted draft prepared for internal board review. Verify figures against the live dashboard before external distribution.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
